import { useMutation } from '@tanstack/react-query'

import { Task } from '@sparkai/shared-types'

import { apiClient } from '../apiClient'

type ManualAuditResponse = {
  tasks: Task[]
}

type ManualAuditRequest = {
  programIds: string[]
  operatorIds: string[]
  taskCount: number
  dateRange: Date[]
  isResolutionStart: boolean
}

type AuditTask = Task & {
  audit_task_id: string
}

type GenerateAuditResponse = {
  audit_task_id: string
  data: AuditTask
}[]

type GeneratedAuditSession = {
  tasks: AuditTask[]
}

type Annotation = {
  type: string
  label: string
  metadata: unknown | null
  vertices: {
    x: number
    y: number
  }[]
  image_idx: number | null
  annotation_id?: string | null
  engagement_level: boolean
  original_annotation_id: string
}

type Image = {
  filename: string | null
  image_id: string
  location: string
  thumbnail_sm_location: string | null
  thumbnail_lg_location: string | null
}

type Resolution = {
  resolution_id: string
  date_created: string
  date_updated: string
  mark_delete: boolean
  owner_id: string
  program_id: string
  engagement_id: string
  original_annotation_ids: string[]
  annotation_ids: string[]
  image_ids: string[] | null
  video_ids: string[] | null
  response: string
  ground_truth: boolean
  training_id: string | null
  task_id: string
  subrequest_ids: string[] | null
  answers: string[]
  time_to_close: number
  operator_session_id: string | null
  consensus: boolean
  is_aggregate: boolean
  annotations: Annotation[] | null
}

type SubRequest = {
  answers: string[]
  question: string[]
  subrequest_id: string
}

type DiscrepancyEngagement = {
  engagement_id: string
  program_id: string
  subengagement_ids: string[] | null
  client_id: string
  date_created: string
  date_closed: string | null
  state: string
  metadata: unknown | null
  webhook_url: unknown | null
  instructions: string | null
  aggregate_resolution_id: string
  time_to_close: string | null
  time_to_task: string | null
  subrequests: SubRequest[] | null
  beq_is_under_agreement: string | null
  associated_engagement_id: string | null
  annotations: Annotation[] | null
  images: Image[] | null
  videos: string | null
  program: {
    name: string
    engagement_lifetime: number
    icon_type: string | null
    color: string
    instructions: string
    display_name: string
    operator_display_name: string | null
    icon: string
  }
}

type GeneratedAuditDiscrepancySession = {
  audit_discrepancy_id: string
  engagement: DiscrepancyEngagement
  aggregate_resolution: Resolution
  votes: Resolution[]
}

export function useGenerateManualAuditSession() {
  return useMutation({
    mutationFn: async (values: ManualAuditRequest) => {
      const body = {
        program_ids: values.programIds,
        operator_ids: values.operatorIds,
        taskCount: values.taskCount,
        dateRange: values.dateRange.map((value) => value.toISOString()),
        isResolutionStart: values.isResolutionStart,
      }
      const { data } = await apiClient.post<ManualAuditResponse>('int/audit/generate', body)
      return data
    },
  })
}

export function useGenerateAutomatedAuditSession() {
  return useMutation({
    mutationFn: async (programId?: string): Promise<GeneratedAuditSession> => {
      const { data } = await apiClient.post<GenerateAuditResponse>('/int/v2/audit/generate', {
        program_id: programId,
        limit: 50,
      })
      const tasks = data.map((auditTask) => ({ ...auditTask.data, audit_task_id: auditTask.audit_task_id }))
      return { tasks }
    },
  })
}

export function useGenerateAutomatedAuditDiscrepancySession() {
  return useMutation({
    mutationFn: async (programId?: string): Promise<GeneratedAuditDiscrepancySession[]> => {
      const { data } = await apiClient.post<GeneratedAuditDiscrepancySession[]>(
        '/int/v2/audit/review/generate',
        {
          programId: programId,
        }
      )
      return data
    },
  })
}
